import type { ColumnConfig } from '@/entities/data-export'
import type { FieldSchemaModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { ExportFieldsFormData } from './ExportFieldsModal'
import type { ExportParamsFormData } from './ExportParamsModal'

import React, { createContext, use, useCallback, useState } from 'react'
import { find } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { ampli } from '@/shared/lib/ampli'

export type ExportConfigurationContextValue = {
	kind: string
	isAdditional?: boolean
	isLoading?: boolean
	schema: FieldSchemaModel[]
	state: {
		step: 'fields' | 'params'
		exportDestination: 'local' | 'external'
		fields: ColumnConfig[]
		draftFormData: Partial<ExportParamsFormData & ExportFieldsFormData>
		setStep(step: 'fields' | 'params'): void
		setFields(fields: ColumnConfig[]): void
		setExportDestination(destination: 'local' | 'external'): void
	}
	options: {
		fields_preset: {
			value: string
			label: string
			preset: ColumnConfig[]
		}[]
		format: {
			value: string
			label: string
		}[]
		aggregation: {
			value: string
			label: string
		}[]
	}
	handleColumnsSubmit(data: ExportFieldsFormData): void
	handleParamsSubmit(data: ExportParamsFormData): void
}

export const ExportConfigurationContext = createContext(
	{} as ExportConfigurationContextValue
)
export type ExportConfigurationProviderProps = {
	/**
	 * Unique identifier of the export kind (e.g. 'assortment', 'bi_revenue')
	 */
	kind: string

	/**
	 * Default values for the export parameters form
	 */
	defaultValues: Partial<ExportParamsFormData>

	/**
	 * Schema of the export fields
	 */
	schema: FieldSchemaModel[]
	/**
	 * Whether the export is not assortment-related
	 */
	isAdditional?: boolean
	isLoading?: boolean
	fieldsPresetOptions?: {
		value: string
		label: string
		preset: ColumnConfig[]
	}[]
	onSubmit(data: ExportParamsFormData & ExportFieldsFormData): void
	children: React.ReactNode
}
export default function ExportConfigurationProvider({
	kind,
	children,
	onSubmit,
	fieldsPresetOptions,
	defaultValues,
	isAdditional,
	isLoading,
	schema,
}: ExportConfigurationProviderProps) {
	const { t } = useTranslation()

	const [step, setStep] = useState<'fields' | 'params'>('params')
	const [fields, setFields] = useState<ColumnConfig[]>([])
	const [exportDestination, setExportDestination] = useState<
		'local' | 'external'
	>('local')
	const [draftFormData, setDraftFormData] = useState<
		Partial<ExportParamsFormData & ExportFieldsFormData>
	>({
		...(defaultValues || {}),
	})
	const getFieldsPreset = useCallback(
		(value: string) =>
			find(
				{
					value,
				},
				fieldsPresetOptions
			)?.preset || [],
		[fieldsPresetOptions]
	)

	const handleColumnsSubmit = (formData: ExportFieldsFormData) => {
		onSubmit({
			exportDestination,
			...draftFormData,
			...formData,
		} as ExportParamsFormData &
			ExportFieldsFormData & { exportDestination: 'local' | 'external' })
	}
	const handleParamsSubmit = (formData: ExportParamsFormData) => {
		const selectedPreset = getFieldsPreset(formData.fields_preset)
		if (exportDestination === 'external') {
			onSubmit({
				exportDestination,
				...formData,
				fields: selectedPreset,
			} as ExportParamsFormData &
				ExportFieldsFormData & { exportDestination: 'local' | 'external' })
		} else {
			ampli.exportParamsSetParams({
				export_kind: kind,
				name: formData.name,
				fields_preset: formData.fields_preset,
				format: formData.format,
				aggregation: formData.aggregation,
			})
			setStep('fields')
			setDraftFormData(formData)
			setFields(selectedPreset)
		}
	}

	return (
		<ExportConfigurationContext
			value={{
				kind,
				isAdditional,
				isLoading,
				state: {
					step,
					setStep,
					fields,
					setFields,
					exportDestination,
					setExportDestination,
					draftFormData,
				},
				options: {
					fields_preset: fieldsPresetOptions!,
					format: fileFormatOptions,
					aggregation: getGroupByOptions(t),
				},
				schema,
				handleColumnsSubmit,
				handleParamsSubmit,
			}}
		>
			{children}
		</ExportConfigurationContext>
	)
}
export function useExportConfiguration() {
	return use(ExportConfigurationContext)
}
const fileFormatOptions = [
	{ value: 'xlsx', label: 'Excel' },
	{ value: 'csv', label: 'CSV' },
]
const getGroupByOptions = (t: TFunction) => [
	{ value: 'product', label: t('sku', { defaultValue: 'SKU' }) },
	{
		value: 'pricing_line',
		label: t('pricing_line', { defaultValue: 'Pricing line' }),
	},
]
